import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import type {CreateFlowFeatureControllerToggleProps} from '~/CreateFabricInvitation/types/types';
import {FEATURE_QA_IDS} from '~/CreateFabricInvitation/utils/constants';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {PollOverlay} from '~/common/fabric/CreateDesign/components/Polls/PollOverlay';
import {CREATE_EDIT_POLL} from '~/common/fabric/reducers/widgets/constants';
import {
  selectHasPoll,
  selectIsMealOptionsEventType,
} from '~/common/fabric/reducers/widgets/selectors';
import {EventFeature} from '~/common/types/event';
import {MealOptionsIcon} from '~library/svg/MealOptionsIcon';
import {PollLineIcon} from '~library/svg/PollLineIcon';
import {scrollElementIntoViewById} from '~library/utils/misc';

import {citrine} from '~libSass/base/_exports.sass';

export const PollsToggle = ({
  active,
  renderAs = 'block',
  hide = false,
}: CreateFlowFeatureControllerToggleProps) => {
  const store = useStore();
  const hasPoll = useSelector(selectHasPoll);

  const [listenForChanges, setListenForChanges] = useState(false);
  const [pollSwitchOn, setPollSwitchOn] = useState(hasPoll);
  const [showPollOverlay, setShowPollOverlay] = useState(false);
  // biome-ignore lint/suspicious/noExplicitAny: no any
  const eventWidgets = useSelector(({widgets}: {widgets: any}) => widgets);
  const [questionValue, setQuestionValue] = useState<string>(
    eventWidgets?.polls ? eventWidgets?.polls[0]?.question : ''
  );
  const isMealOptions = useSelector(selectIsMealOptionsEventType);

  const panelHeading = useMemo(() => {
    if (renderAs === 'pill' && !showPollOverlay) return isMealOptions ? 'Meal options' : 'Poll';
    return isMealOptions ? 'Get meal options' : 'Poll your guests';
  }, [isMealOptions, renderAs, showPollOverlay]);
  const subtitle = useMemo(() => {
    if (isMealOptions) {
      return hasPoll
        ? 'Edit your question & response options.'
        : 'Find out what your guests want to eat.';
    }
    return hasPoll ? questionValue : 'Get pre-party info to help you plan.';
  }, [hasPoll, isMealOptions, questionValue]);

  const clearPollData = () => {
    store.dispatch({
      type: CREATE_EDIT_POLL,
      payload: {polls: []},
    });
  };

  const resetPollState = () => {
    clearPollData();
    setShowPollOverlay(false);
    setListenForChanges(true);
  };

  const handlePollPanelClose = useCallback((poll?: boolean) => {
    if (!poll) {
      setPollSwitchOn(false);
    }
    setListenForChanges(false);
    setShowPollOverlay(false);
  }, []);

  const overlayCallback = useCallback((pollWidget: {question: string}) => {
    setPollSwitchOn(true);
    setQuestionValue(pollWidget.question);
  }, []);

  const handleOnClick = useCallback(() => {
    if (!pollSwitchOn) {
      setPollSwitchOn(true);
    }
    if (hasPoll) scrollElementIntoViewById('module-poll', 'center', 0);
    setShowPollOverlay(true);
  }, [pollSwitchOn, hasPoll]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (listenForChanges) {
      if (!hasPoll) {
        setShowPollOverlay(true);
      } else {
        resetPollState();
      }
    } else if (!hasPoll) {
      setListenForChanges(true);
    } else if (!pollSwitchOn) {
      resetPollState();
    }
  }, [pollSwitchOn]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    if (!hasPoll) {
      resetPollState();
    } else {
      handlePollPanelClose(!!eventWidgets?.polls[0]);
    }
  }, [hasPoll]);

  let qaId: string = '';
  if (typeof FEATURE_QA_IDS[EventFeature.POLLS] === 'string') {
    qaId = FEATURE_QA_IDS[EventFeature.POLLS];
  } else {
    qaId = FEATURE_QA_IDS[EventFeature.POLLS](store);
  }

  return (
    <InvitationWidget.Toggle
      editable={hasPoll}
      filled={active}
      title={panelHeading}
      subtitle={subtitle}
      qaId={qaId}
      onClick={handleOnClick}
      renderAs={renderAs}
      hide={hide}
      startAdornment={
        isMealOptions ? (
          <MealOptionsIcon ratio={0.75} color={citrine} />
        ) : (
          <PollLineIcon ratio={0.75} color={citrine} />
        )
      }
      overlay={
        <PollOverlay
          isOpen={showPollOverlay}
          onClose={handlePollPanelClose}
          overlayCallback={overlayCallback}
          title={panelHeading}
          isMealOptions={isMealOptions}
          hasPoll={hasPoll}
        />
      }
    />
  );
};
